/**
 * TarifEditModal — edit one tarif (per-section modal). Create is the full-page `TarifForm`; both
 * share `TarifFormBody`. PUTs a slice-only payload.
 */

import { Button } from '@/components/Button';
import { Modal, DialogCrown } from '@/components/Modal';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { useUpdateTarifMutation, type PujcovnaTarif } from './api';
import { TARIF_FORM_ID, TarifFormBody, tarifSeed, tarifToBody } from './tarif-section-forms';

interface TarifEditModalProps {
  tarif: PujcovnaTarif;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TarifEditModal({ tarif, open, onOpenChange }: TarifEditModalProps) {
  const update = useUpdateTarifMutation(tarif.id);

  return (
    <Modal open={open} onOpenChange={onOpenChange} maxWidth={860}>
      <DialogCrown
        eyebrow="Tarif"
        title={
          <>
            Upravit <em>{tarif.nazev}</em>
          </>
        }
        sub="Název, odkaz na obchodní podmínky a ceník tarifu."
      />
      <Modal.Body className="px-8 py-5">
        <TarifFormBody
          initial={tarifSeed(tarif)}
          busy={update.isPending}
          onSubmit={(form) => {
            if (!form.nazev.trim()) {
              toast.error('Zadejte název tarifu.');
              return;
            }
            const body = tarifToBody(form, tarif);
            if (!body.ok) {
              toast.error(body.chyba);
              return;
            }
            update.mutate(body.body, {
              onSuccess: () => {
                toast.ok('Tarif uložen.');
                onOpenChange(false);
              },
              onError: (err) => toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se uložit tarif.'}`),
            });
          }}
        />
      </Modal.Body>
      <Modal.Foot
        actions={
          <>
            <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={update.isPending}>
              Zrušit
            </Button>
            <Button variant="ink" type="submit" form={TARIF_FORM_ID} disabled={update.isPending}>
              Uložit
            </Button>
          </>
        }
      />
    </Modal>
  );
}
